import type { InventoryScrapped } from 'app/typings/autoInventory';
import { ModifierType } from 'app/typings/autoInventory';
import type { BookingcomRoomWithInventory } from 'app/typings/core/extranet/inventory/autoInventory';
import { formatDateWithoutHour } from 'app/utils/dates';
import { addDays, eachDayOfInterval } from 'app/utils/dayjs';

const SMART_INVENTORY_OVERBOOKING_LIMIT = 1;

export type PreviewRoomMappings = {
  scrappedRoomId?: number;
  scrappedRoomName?: string;
  staycationRoom?: {
    id: number;
    name: string;
  };
};

export type PreviewFormValues = {
  roomMappings: PreviewRoomMappings[];
  matchOTAPrice?: boolean;
  customBar: {
    value?: number;
    modifierType: ModifierType.FLAT | ModifierType.RATE;
  };
};

export type RoomWithInventory = {
  id: number;
  name: string;
  inventory: InventoryScrapped[];
};

export type OrganizedInventory = {
  dates: string[];
  stocks: number[];
  bars: number[];
};

const createEmptyRoomInventory = (): InventoryScrapped[] => {
  const now = Date.now();
  const dates = eachDayOfInterval({ start: now, end: addDays(now, 6) });

  return dates.map((date) => ({
    date: formatDateWithoutHour(date),
    stock: 0,
    price: 0,
  }));
};

const getBarPrice = (price: number, formData: PreviewFormValues): number => {
  if (price === 0) {
    return price;
  }

  if (formData.matchOTAPrice) {
    return price;
  }

  if (
    formData.customBar?.value &&
    formData.customBar?.modifierType === ModifierType.RATE
  ) {
    return price / (1 + formData.customBar.value / 100);
  }

  return price - (formData.customBar?.value ?? 0);
};

export const associateRooms = (
  formData: PreviewFormValues,
  roomsScrapped: BookingcomRoomWithInventory[]
): RoomWithInventory[] => {
  if (!formData?.roomMappings) {
    return [];
  }

  return Object.values(formData.roomMappings).reduce<RoomWithInventory[]>(
    (acc, room) => {
      if (!room?.scrappedRoomId) {
        return acc;
      }

      const roomScrapped = roomsScrapped.find(
        (data) => data.roomId === room.scrappedRoomId
      );

      acc.push({
        id: room.scrappedRoomId,
        name: room.staycationRoom?.name ?? 'Unknown',
        inventory: roomScrapped?.inventory ?? createEmptyRoomInventory(),
      });

      return acc;
    },
    []
  );
};

export const organizeInventoryData = (
  inventory: InventoryScrapped[],
  formData: PreviewFormValues
): OrganizedInventory => {
  const now = Date.now();
  const dates = eachDayOfInterval({ start: now, end: addDays(now, 6) });
  const dateStrings = dates.map((d) => formatDateWithoutHour(d));

  const inventoryMap = new Map(inventory.map((item) => [item.date, item]));

  return dateStrings.reduce<OrganizedInventory>(
    (acc, date) => {
      const item = inventoryMap.get(date) ?? { stock: 0, price: 0 };
      const rawStock = item.stock - SMART_INVENTORY_OVERBOOKING_LIMIT;

      acc.dates.push(date);
      acc.stocks.push(rawStock < 0 ? 0 : rawStock);
      acc.bars.push(getBarPrice(item.price, formData));

      return acc;
    },
    { dates: [], stocks: [], bars: [] }
  );
};
